import { useEffect } from 'react';
import { useAuth } from './useAuth';
import { apiFetch, getCachedAccessToken } from '@/lib/api-fetch';
import { apiUrl } from '@/lib/api-url';

/**
 * Hook to track user's online status
 * Updates status on mount/unmount and page visibility changes
 */
export function useOnlineStatus() {
  const { profile } = useAuth();

  useEffect(() => {
    if (!profile) return;

    const updateOnlineStatus = async (isOnline: boolean, gameId?: string) => {
      try {
        await apiFetch('/api/online-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isOnline, gameId }),
        });
      } catch (error) {
        console.error('Failed to update online status:', error);
      }
    };

    // Set online on mount
    updateOnlineStatus(true);

    // Handle page visibility
    const handleVisibilityChange = () => {
      updateOnlineStatus(!document.hidden);
    };

    // Handle page unload — sendBeacon cannot attach Bearer auth; use keepalive fetch
    const handleBeforeUnload = () => {
      const token = getCachedAccessToken();
      if (!token) return;
      const url = apiUrl('/api/online-status');
      const body = JSON.stringify({ isOnline: false });
      try {
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body,
        });
      } catch {
        /* tab closing */
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Set offline on unmount
      updateOnlineStatus(false);
    };
  }, [profile]);
}
